import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "../../api";
import { SectionTitle, StatusPill, EmptyState, Avatar, ShowMore } from "../../components/Atoms";
import { AIAuditButton, useAIAudit } from "../../components/AIAuditPanel";
import { Plus, PencilSimple, Trash, Users, InstagramLogo, MapPin, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = { name:"", email:"", phone:"", city:"", college:"", gender:"", age:"", instagram:"", instagram_followers:0,
  youtube:"", linkedin:"", skills:[], photo:"", bio:"", status:"Active" };
const GENDER_OPTIONS = ["Male", "Female", "Prefer not to say"];

export default function AmbassadorsPage() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { openAudit, AuditModal } = useAIAudit();
  const [visible, setVisible] = useState(12);

  const load = async () => { const { data } = await api.get("/ambassadors"); setList(data); };
  useEffect(() => { load(); }, []);

  const filtered = list.filter(a => (a.name + a.city + a.college).toLowerCase().includes(search.toLowerCase()));

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, instagram_followers: Number(form.instagram_followers || 0),
        age: form.age === "" || form.age == null ? null : Number(form.age),
        skills: Array.isArray(form.skills) ? form.skills : String(form.skills).split(",").map(s => s.trim()).filter(Boolean) };
      if (editing) await api.put(`/ambassadors/${editing}`, payload);
      else await api.post("/ambassadors", payload);
      toast.success(editing ? "Ambassador updated" : "Ambassador added");
      setOpen(false); setEditing(null); setForm(EMPTY); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (id) => { if (window.confirm("Delete?")) { await api.delete(`/ambassadors/${id}`); toast.success("Deleted"); load(); } };

  return (
    <div className="space-y-5">
      <SectionTitle title="Ambassadors" subtitle="Your Gen Z creator roster"
        action={<button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} className="btn-primary flex items-center gap-2" data-testid="new-ambassador-btn"><Plus size={16} weight="bold" /> New</button>} />

      <input placeholder="Search by name, city, college…" value={search} onChange={e => setSearch(e.target.value)} className="input-base" data-testid="ambassador-search" />

      {filtered.length === 0 ? <EmptyState icon={Users} /> : (
        <>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.slice(0, visible).map(a => (
            <div key={a.id} className="glass p-5 hover-lift" data-testid={`ambassador-card-${a.id}`}>
              <Link to={`/admin/ambassadors/${a.id}`} className="block" data-testid={`open-ambassador-${a.id}`}>
                <div className="flex items-center gap-3">
                  <Avatar src={a.photo} name={a.name} size={48} />
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-bold truncate hover:text-[#22D3EE]">{a.name}</div>
                    <div className="text-xs text-muted flex items-center gap-1 truncate"><MapPin size={12} /> {a.city || "—"} · {a.college || "—"}</div>
                  </div>
                  <StatusPill status={a.status} />
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-white/5 rounded-xl px-3 py-2"><div className="text-muted">Followers</div><div className="text-white font-bold">{(a.instagram_followers || 0).toLocaleString()}</div></div>
                  <div className="bg-white/5 rounded-xl px-3 py-2"><div className="text-muted">Score</div><div className="text-white font-bold">{a.performance_score || 0}</div></div>
                </div>
                {a.instagram && <div className="flex items-center gap-1.5 text-xs text-[#EC4899] mt-3"><InstagramLogo size={14} weight="bold" /> {a.instagram}</div>}
                <div className="text-[10px] text-[#22D3EE] mt-3 flex items-center justify-end gap-1 font-bold">View profile <ArrowRight size={12} weight="bold" /></div>
              </Link>
              <div className="flex gap-2 mt-3">
                <AIAuditButton ambassadorId={a.id} onClick={() => openAudit(a.id)} testIdSuffix={`list-${a.id}`} />
                <button onClick={() => { setEditing(a.id); setForm({ ...EMPTY, ...a, skills: (a.skills || []).join(", ") }); setOpen(true); }} className="btn-ghost flex-1 flex items-center justify-center gap-2 text-sm" data-testid={`edit-ambassador-${a.id}`}><PencilSimple size={14} /> Edit</button>
                <button onClick={() => remove(a.id)} className="btn-ghost text-[#EC4899] text-sm" data-testid={`delete-ambassador-${a.id}`}><Trash size={14} /></button>
              </div>
            </div>
          ))}
        </div>
        <ShowMore visible={visible} total={filtered.length} onMore={setVisible} step={12} testid="ambassadors-show-more" />
        </>
      )}

      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4" onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <form onSubmit={save} className="glass relative p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto space-y-3" onClick={e => e.stopPropagation()}>
            <div className="text-white text-xl font-bold mb-2">{editing ? "Edit Ambassador" : "New Ambassador"}</div>
            {[["name","Name"],["email","Email"],["phone","Phone"],["city","City"],["college","College"],
              ["instagram","Instagram handle"],["instagram_followers","Followers"],["youtube","YouTube"],
              ["linkedin","LinkedIn"],["photo","Photo URL"],["skills","Skills (comma)"],["bio","Bio"]].map(([k,l]) => (
              <div key={k}>
                <label className="text-xs uppercase font-bold text-muted tracking-wider">{l}</label>
                {k === "bio" ? (
                  <textarea value={form[k] || ""} onChange={e => setForm({ ...form, [k]: e.target.value })} className="input-base mt-1" rows={3} />
                ) : (
                  <input value={form[k] ?? ""} onChange={e => setForm({ ...form, [k]: e.target.value })} className="input-base mt-1" required={k === "name" || k === "email"} type={k === "instagram_followers" ? "number" : "text"} />
                )}
              </div>
            ))}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase font-bold text-muted tracking-wider">Gender</label>
                <select value={form.gender || ""} onChange={e => setForm({ ...form, gender: e.target.value })} className="input-base mt-1" data-testid="admin-amb-gender">
                  <option value="">—</option>
                  {GENDER_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase font-bold text-muted tracking-wider">Age</label>
                <input type="number" min="0" value={form.age ?? ""} onChange={e => setForm({ ...form, age: e.target.value })} className="input-base mt-1" data-testid="admin-amb-age" />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase font-bold text-muted tracking-wider">Status</label>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="input-base mt-1">
                <option>Active</option><option>Pending</option><option>Blocked</option>
              </select>
            </div>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost flex-1">Cancel</button>
              <button type="submit" className="btn-primary flex-1" data-testid="save-ambassador-btn">{editing ? "Save" : "Create"}</button>
            </div>
          </form>
        </div>
      )}
      {AuditModal}
    </div>
  );
}
